"use client";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { buttonClass } from "@/components/ui/button";

/** Sole switch for this link: ENABLED = renders, absent/DISABLED = today's page. */
export const COMPANY_EMPLOYER_LINK_FLAG = "company-employer-brief-link";

/**
 * Distribution into the employer hub from company profile pages. A hiring
 * manager checking how their company appears here is the highest-intent
 * employer visitor on the seeker side. Offers the free brief only; seekers are
 * never asked to pay. Client island (the page is edge-cached, so the flag must
 * not be read server-side). Off/absent renders nothing.
 */
export function CompanyEmployerLink({ locale }: { locale: "id" | "en" }) {
	const enabled = useFeatureFlagEnabled(COMPANY_EMPLOYER_LINK_FLAG) === true;
	const posthog = usePostHog();
	const viewedRef = useRef(false);
	const isId = locale === "id";

	useEffect(() => {
		if (!enabled || viewedRef.current) return;
		viewedRef.current = true;
		posthog?.capture("company_employer_link_viewed", { locale });
	}, [enabled, posthog, locale]);

	if (!enabled) return null;

	return (
		<section
			aria-label={isId ? "Untuk perekrut" : "For employers"}
			data-testid="company-employer-link"
			className="mt-8 flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
		>
			<p className="text-sm text-foreground">
				{isId
					? "Sedang merekrut? Kirim brief gratis dan kami cocokkan dengan kandidat dari pool kami."
					: "Hiring? Send a free brief and we will match it to candidates from our pool."}
			</p>
			<a
				href={`/${locale}/employers?src=company_page#start`}
				data-testid="company-employer-cta"
				onClick={() =>
					posthog?.capture("company_employer_link_clicked", { locale })
				}
				className={buttonClass({ size: "sm", variant: "secondary" })}
			>
				{isId ? "Isi brief gratis" : "Send a free brief"}
			</a>
		</section>
	);
}
